<script setup>

import {onMounted, reactive} from "vue";
import ProgressTrackingReportDiagram from "../../../../components/community/member/progressTrackingReportDiagram.vue";
import ProgressTrackingCheckinsChart from "../../../../components/community/member/progressTrackingCheckinsChart.vue";

const props = defineProps({
    channelId: {
        type: Number,
        required: true,
    },
    memberId: {
        type: Number,
        required: true,
    },
})

const reporting = reactive({
    channel: {},
    member: {},
    statistics: {},
    checkInDays: {},
});

const loadChannel = async () => {
    const res = await axios.get(route('community.admin.api.reporting.member', [props.channelId, props.memberId]));

    reporting.channel = res.data.channel;
    reporting.member = res.data.member;
    reporting.statistics = res.data.statistics;
}

onMounted(() => {
    loadChannel();
});

</script>

<template>
    <div>
        <frosted-bar>
            <template #navigation>
                <a :href="route('community.admin.reporting.channel', props.channelId)" class="mf-link">
                    {{ _mft('community:reporting.members.title') }}
                </a>
            </template>
            <div class="flex items-center justify-between space-x-4">
                <h1>{{ reporting.channel.name ? reporting.channel.name : _mft('community:reporting.title') }}</h1>
            </div>
        </frosted-bar>

        <div class="mt-12">
            <div v-if="reporting.statistics?.post_streaks" class="grid grid-cols-2 gap-4">
                <card-basic class="!mb-0" compact>
                    <template v-slot:body>
                        <h1 class="text-lg">
                            {{ reporting.member.name }}
                        </h1>
                        <p class="text-xs">
                            <a title="Send an email" class="mf-link inline-block mb-1" :href="'mailto:'+reporting.member.email">{{ reporting.member.email }}</a><br>
                            Member since: {{ reporting.member.created_at }}
                        </p>
                    </template>
                </card-basic>

                <card-basic class="!mb-0" compact>
                    <template v-slot:body>
                        <h1 class="text-lg">
                            Badges
                        </h1>
                        <ul class="flex mt-1">
                            <li
                                v-for="(badge, b) in reporting.member.badges"
                                :key="b"
                                class="pb-1 pr-1"
                            >
                                <pill class="bg-gray-200">
                                    {{ badge.name }}
                                </pill>
                            </li>
                            <li
                                v-if="reporting.member.badges.length === 0"
                                class="text-gray-400 text-xs pt-0.5"
                            >
                                No badges
                            </li>
                        </ul>
                    </template>
                </card-basic>

                <card-basic class="!mb-0" compact>
                    <template v-slot:body>
                        <div class="flex items-end justify-between">
                            <div class="text-right">
                                <h1 class="text-lg">
                                    {{ reporting.statistics.post_streaks.max_daily_streak }} days
                                </h1>
                            </div>
                        </div>
                        <p class="text-xs">Longest daily streak</p>
                    </template>
                </card-basic>

                <card-basic class="!mb-0" compact>
                    <template v-slot:body>
                        <div class="flex items-end justify-between">
                            <div class="text-right">
                                <h1 class="text-lg">
                                    {{ reporting.statistics.post_streaks.current_daily_streak }} days
                                </h1>
                            </div>
                        </div>
                        <p class="text-xs">Current daily streak</p>
                    </template>
                </card-basic>

                <card-basic class="!mb-0" compact>
                    <template v-slot:body>
                        <div class="flex items-end justify-between">
                            <div class="text-right">
                                <h1 class="text-lg">
                                    {{ reporting.statistics.post_streaks.max_reminder_days_streak }} days
                                </h1>
                            </div>
                        </div>
                        <p class="text-xs">Longest daily streak on days with reminders</p>
                    </template>
                </card-basic>

                <card-basic class="!mb-0" compact>
                    <template v-slot:body>
                        <div class="flex items-end justify-between">
                            <div class="text-right">
                                <h1 class="text-lg">
                                    {{ reporting.statistics.post_streaks.current_reminder_days_streak }} days
                                </h1>
                            </div>
                        </div>
                        <p class="text-xs">Current daily streak on days with reminders</p>
                    </template>
                </card-basic>
            </div>

        </div>

        <div class="mt-8 mb-2">
            <ProgressTrackingCheckinsChart
                :data="reporting.statistics.check_in_days"
            >
                <template #header>
                    <h3>
                        Check-ins
                    </h3>
                </template>
            </ProgressTrackingCheckinsChart>
        </div>

        <div v-if="reporting.statistics?.question_trends" class="mt-8">
            <p class="text-center text-gray-500 text-xs">Only fields with data that can be presented as a graph are displayed; Number, Yes/No and Completion.</p>

            <ul class="space-y-4">
                <li v-for="(trend, index) in reporting.statistics.question_trends" :key="index">
                    <card-basic :compact="true">
                        <h3 class="mb-2">{{ trend.question }}</h3>
                        <ProgressTrackingReportDiagram
                            :data="trend.daily_responses"
                            :question="trend.question"
                            :question-type="trend.type"
                        />
                    </card-basic>
                </li>
            </ul>
        </div>

        <p class="text-center text-gray-500 text-xs">If there are fields or questions missing in the report, it usually means the member hasn't responded to those questions.</p>

    </div>
</template>
